export const NOTIFICATION_SOURCES = ['security', 'work', 'cs', 'system']
export const NOTIFICATION_ROUTE_NAMES = [
  'NotificationCenter',
  'SecurityAlertRecipients',
  'SecurityAlertList',
  'SecurityAlertDetail',
]

export const NOTIFICATION_TABS = [
  { value: 'security', text: '보안 경보 알림' },
  { value: 'work', text: '할일 알림' },
  { value: 'cs', text: 'CS 알림' },
  { value: 'system', text: '시스템 알림' },
]

const NOTIFICATION_SOURCE_LABELS = {
  security: '보안',
  work: '할일',
  cs: 'CS',
  system: '시스템',
}

export async function markUnreadNotifications(items = [], markRead, options = {}) {
  const unreadItems = items.filter(item => !item.isRead)
  const concurrency = Math.min(
    unreadItems.length,
    Math.max(1, Number(options.concurrency) || 5),
  )
  let nextIndex = 0
  let updatedCount = 0
  let failedCount = 0

  async function runWorker() {
    while (nextIndex < unreadItems.length) {
      const item = unreadItems[nextIndex]
      nextIndex += 1
      try {
        await markRead(item)
        updatedCount += 1
      } catch (error) {
        failedCount += 1
      }
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => runWorker()))
  return failedCount > 0
    ? { updated_count: updatedCount, failed_count: failedCount }
    : { updated_count: updatedCount }
}

export function createNotificationReadAllHandlers(dependencies = {}) {
  return {
    security() {
      return dependencies.markAllSecurityNotificationsRead()
    },
    async work() {
      const items = await dependencies.getUnreadWorkNotifications()
      return markUnreadNotifications(items, dependencies.markWorkNotificationRead)
    },
    async cs() {
      const items = await dependencies.getCsNotifications()
      return markUnreadNotifications(items, dependencies.markCsNotificationRead)
    },
    async system() {
      const items = await dependencies.getSystemNotifications()
      return markUnreadNotifications(items, dependencies.markSystemNotificationRead)
    },
  }
}

export function notificationReadAllFeedback(source, updatedCount = 0, failedCount = 0) {
  const label = NOTIFICATION_SOURCE_LABELS[source] || '알림'
  const feedback = {
    success: `${label} 알림 ${Number(updatedCount || 0)}건을 읽음 처리했습니다.`,
    failure: `${label} 알림을 읽음 처리하지 못했습니다.`,
  }
  if (Number(failedCount || 0) > 0) {
    feedback.partial = `${label} 알림 ${Number(updatedCount || 0)}건을 읽음 처리했고 ${Number(failedCount)}건은 처리하지 못했습니다.`
  }
  return feedback
}

export function createNotificationRequestCoordinator(sources = NOTIFICATION_SOURCES) {
  let generation = 0
  let versions = sources.reduce((result, source) => {
    result[source] = 0
    return result
  }, {})

  return {
    begin(source) {
      versions[source] = Number(versions[source] || 0) + 1
      return { source, generation, version: versions[source] }
    },
    isCurrent(request) {
      return Boolean(request)
        && request.generation === generation
        && request.version === versions[request.source]
    },
    reset() {
      generation += 1
      versions = sources.reduce((result, source) => {
        result[source] = 0
        return result
      }, {})
    },
  }
}

export function notificationTabLocation(source) {
  return {
    name: 'NotificationCenter',
    query: { tab: NOTIFICATION_SOURCES.includes(source) ? source : 'security' },
  }
}

export function isNotificationRouteName(routeName) {
  return NOTIFICATION_ROUTE_NAMES.includes(routeName)
}

export function createPageNumbers(maxPage) {
  return Array.from(
    { length: Math.max(1, Number(maxPage) || 1) },
    (_, index) => index + 1,
  )
}

export function normalizeSecurityNotification(notification = {}) {
  const hasAlert = notification.alert !== null && notification.alert !== undefined
  return {
    key: `security:${notification.id}`,
    source: 'security',
    id: notification.id,
    title: notification.title || '보안 경보 알림',
    message: notification.message || '',
    createdAt: notification.created_at || '',
    isRead: notification.is_read === true || Boolean(notification.read_at),
    target: {
      kind: 'route',
      location: hasAlert
        ? { name: 'SecurityAlertDetail', params: { alertId: notification.alert } }
        : notificationTabLocation('security'),
    },
    raw: notification,
  }
}

export function normalizeWorkNotification(notification = {}) {
  const workName = notification.work_name || notification.work?.name || ''
  const itemMessage = notification.work_item_name || notification.work_item?.name || notification.body || notification.message || ''
  return {
    key: `work:${notification.id}`,
    source: 'work',
    id: notification.id,
    title: notification.title || '할일 알림',
    message: [workName, itemMessage].filter(Boolean).join(' · '),
    createdAt: notification.created_at || '',
    isRead: notification.read !== undefined ? Boolean(notification.read) : Boolean(notification.read_at),
    target: {
      kind: 'work',
      workId: notification.work_id || null,
      workItemId: notification.work_item_id || null,
      runtimeNavigation: notification.runtime_navigation || notification.work_item?.runtime_navigation || null,
    },
    raw: notification,
  }
}

export function normalizeCsNotification(notification = {}) {
  const recipientRole = notification.assignmentType || notification.recipient?.role
  const recipientRoleLabel = recipientRole === 'technical' ? '기술 담당' : recipientRole === 'cs' ? 'CS 담당' : ''
  return {
    key: `cs:${notification.id}`,
    source: 'cs',
    id: notification.id,
    title: notification.title || notification.kind || 'CS 알림',
    message: notification.message || '',
    createdAt: notification.createdAt || '',
    isRead: Boolean(notification.isRead),
    recipientLabel: notification.recipient?.name && recipientRoleLabel
      ? `${notification.recipient.name} · ${recipientRoleLabel}`
      : '',
    target: notification.ticketId
      ? { kind: 'route', location: { name: 'CSIssueDetail', params: { ticketId: notification.ticketId } } }
      : { kind: 'route', location: notificationTabLocation('cs') },
    raw: notification,
  }
}

export function normalizeSystemNotification(notification = {}) {
  return {
    key: `system:${notification.id}`,
    source: 'system',
    id: notification.id,
    title: notification.title || '시스템 알림',
    message: notification.message || '',
    createdAt: notification.createdAt || '',
    isRead: Boolean(notification.isRead),
    target: {
      kind: 'route',
      location: notificationTabLocation('system'),
    },
    raw: notification,
  }
}

export function buildRecipientPatch(initialSelected, currentSelected) {
  const initial = new Set(initialSelected || [])
  const current = new Set(currentSelected || [])
  return {
    add_user_ids: Array.from(current).filter(id => !initial.has(id)).sort((a, b) => a - b),
    remove_user_ids: Array.from(initial).filter(id => !current.has(id)).sort((a, b) => a - b),
  }
}

export function mergeUnreadSummary(itemsBySource = {}, explicitUnreadCounts = {}, previewLimit = 5) {
  const unreadBySource = {}
  const previewCandidates = []

  NOTIFICATION_SOURCES.forEach(source => {
    const items = Array.isArray(itemsBySource[source]) ? itemsBySource[source] : []
    const unreadItems = items.filter(item => !item.isRead)
    unreadBySource[source] = Number.isFinite(explicitUnreadCounts[source])
      ? Number(explicitUnreadCounts[source])
      : unreadItems.length
    previewCandidates.push(...unreadItems)
  })

  const previewItems = previewCandidates
    .slice()
    .sort((left, right) => {
      const dateDifference = new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime()
      return dateDifference || String(right.key).localeCompare(String(left.key))
    })
    .slice(0, previewLimit)

  return {
    unreadBySource,
    totalUnread: Object.values(unreadBySource).reduce((total, count) => total + count, 0),
    previewItems,
  }
}

export function paginateLocalNotifications(items = [], options = {}) {
  const pageSize = Math.max(1, Number(options.pageSize) || 30)
  const total = items.length
  const maxPage = Math.max(1, Math.ceil(total / pageSize))
  const page = Math.min(Math.max(1, Number(options.page) || 1), maxPage)
  const sortedItems = items.slice().sort((left, right) => (
    new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime()
  ))
  const start = (page - 1) * pageSize
  return {
    items: sortedItems.slice(start, start + pageSize),
    total,
    page,
    maxPage,
    pageSize,
  }
}
